import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import "../Css/Ourlatestworks.css"
import { API_URL } from '../utils/Function';

export default function Ourlatestworks() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [isHovered, setIsHovered] = useState(false);
  const [particles, setParticles] = useState([]);
  const [cornerParticles, setCornerParticles] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]); // Store all projects for filtering
  const [loading, setLoading] = useState(true);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const navRef = useRef(null);
  const navigate = useNavigate();

  const staticCategories = {
    left: ['All'],
    right: []
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch projects from API - fetch all for categories, limit display to 4
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all projects sorted by display_order
        const allProjectsResponse = await fetch(`${API_URL}/project.php/get?orderBy=display_order&orderDir=ASC`);
        const allProjectsData = await allProjectsResponse.json();
        
        if (allProjectsData.success) {
          const allProjectsList = allProjectsData.projects || [];
          setAllProjects(allProjectsList);
          
          // Display only first 6 projects initially (based on display order)
          setProjects(allProjectsList.slice(0, 6));
          
          const categories = [...new Set(allProjectsList
            .map(project => project.category_name)
            .filter(Boolean)
          )];
          
          setDynamicCategories(categories);
        } else {
          console.error('Error fetching projects:', allProjectsData.error);
          setProjects([]);
          setAllProjects([]);
          setDynamicCategories([]);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([]);
        setAllProjects([]);
        setDynamicCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  // Check scroll position for gradient visibility
  useEffect(() => {
    const checkScroll = () => {
      if (navRef.current && allCategories.length > 5) {
        const { scrollLeft, scrollWidth, clientWidth } = navRef.current;
        setShowLeftGradient(scrollLeft > 0);
        setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    const navElement = navRef.current;
    if (navElement && allCategories.length > 5) {
      navElement.addEventListener('scroll', checkScroll);
      checkScroll(); // Initial check
    }

    return () => {
      if (navElement) {
        navElement.removeEventListener('scroll', checkScroll);
      }
    };
  }, [dynamicCategories]);

  // Group projects by category for filtering - use all projects
  const groupedProjects = {
    'All': allProjects.slice(0, 6), // Show only 4 for "All"
    ...dynamicCategories.reduce((acc, category) => {
      acc[category] = allProjects.filter(project => 
        project.category_name === category
      ).slice(0, 6); // Limit to 4 per category
      return acc;
    }, {})
  };

  // Animation function for category change
  const handleCategoryChange = (category) => {
    if (category === activeFilter) return;
    
    setIsAnimating(true);
    
    setTimeout(() => {
      setActiveFilter(category);
      setIsDropdownOpen(false);
      
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }, 300);
  };

  const generateParticles = (count, x, y) => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: Math.random(),
        x: x || Math.random() * 100,
        y: y || Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 1 + 0.5,
        delay: Math.random() * 0.5
      });
    }
    return newParticles;
  };

  const generateCornerParticles = (count, buttonRect) => {
    if (!buttonRect) return [];
    
    const newParticles = [];
    
    for (let i = 0; i < count; i++) {
      const startX = buttonRect.right - 8;
      const startY = buttonRect.bottom - 8;
      
      const angle = Math.random() * Math.PI / 3 + Math.PI / 6;
      const distance = 80 + Math.random() * 200;
      
      const endX = startX + Math.cos(angle) * distance;
      const endY = startY + Math.sin(angle) * distance;
      
      const midX1 = startX + Math.cos(angle) * distance * 0.2;
      const midY1 = startY + Math.sin(angle) * distance * 0.2;
      const midX2 = startX + Math.cos(angle) * distance * 0.5;
      const midY2 = startY + Math.sin(angle) * distance * 0.5;
      const midX3 = startX + Math.cos(angle) * distance * 0.8;
      const midY3 = startY + Math.sin(angle) * distance * 0.8;
      
      const speedTypes = ['slow', 'medium', 'fast'];
      const speed = speedTypes[Math.floor(Math.random() * speedTypes.length)];
      
      const shapeTypes = ['circle', 'blob', 'drop'];
      const shape = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
      
      const baseColors = [
        'rgba(72, 52, 212, 0.9)',
        'rgba(104, 109, 224, 0.8)',
        'rgba(91, 33, 182, 0.7)',
      ];
      const color = baseColors[Math.floor(Math.random() * baseColors.length)];
      
      newParticles.push({
        id: Math.random(),
        startX: `${startX}px`,
        startY: `${startY}px`,
        endX: `${endX}px`,
        endY: `${endY}px`,
        midX1: `${midX1}px`,
        midY1: `${midY1}px`,
        midX2: `${midX2}px`,
        midY2: `${midY2}px`,
        midX3: `${midX3}px`,
        midY3: `${midY3}px`,
        size: Math.random() * 5 + 2,
        color: color,
        speed: speed,
        shape: shape,
        opacity: Math.random() * 0.7 + 0.3
      });
    }
    return newParticles;
  };

  const handleMouseEnter = (e) => {
    setIsHovered(true);
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setParticles(generateParticles(8, 50, 50));
      setCornerParticles(generateCornerParticles(6, buttonRect));
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTimeout(() => {
      setParticles([]);
      setTimeout(() => setCornerParticles([]), 1000);
    }, 500);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleCategorySelect = (category) => {
    handleCategoryChange(category);
  };

  // Handle project click to navigate to details page with slug
  const handleProjectClick = (project) => {
    scrollToTop();
    setTimeout(() => {
      if (project.slug) {
        navigate(`/project/${project.slug}`);
      } else {
        // Fallback to ID if slug doesn't exist
        navigate(`/project/${project.id}`);
      }
    }, 100);
  };

  // Handle view all projects click - navigate to projects page
  const handleViewAllProjects = () => {
    scrollToTop();
    setTimeout(() => {
      navigate('/projects');
    }, 100);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isHovered && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      
      const particleInterval = setInterval(() => {
        setParticles(prev => [...prev, ...generateParticles(2)]);
      }, 200);
      
      const cornerParticleInterval = setInterval(() => {
        setCornerParticles(prev => {
          const updatedParticles = prev.filter(p => 
            Date.now() - p.createdAt < 4000
          );
          
          const newParticles = generateCornerParticles(2, buttonRect).map(p => ({
            ...p,
            createdAt: Date.now()
          }));
          
          return [...updatedParticles, ...newParticles];
        });
      }, 300);
      
      return () => {
        clearInterval(particleInterval);
        clearInterval(cornerParticleInterval);
      };
    }
  }, [isHovered]);

  // Update particle positions on scroll/resize
  useEffect(() => {
    const updateParticles = () => {
      if (isHovered && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        setCornerParticles(prev => 
          prev.map(particle => ({
            ...particle,
            startX: `${buttonRect.right - 8}px`,
            startY: `${buttonRect.bottom - 8}px`
          }))
        );
      }
    };

    window.addEventListener('scroll', updateParticles);
    window.addEventListener('resize', updateParticles);

    return () => {
      window.removeEventListener('scroll', updateParticles);
      window.removeEventListener('resize', updateParticles);
    };
  }, [isHovered]);

  const filteredProjects = groupedProjects[activeFilter] || [];

  // Function to get project image
  const getProjectImage = (project) => {
    // Helper function to construct full URL
    const constructImageUrl = (imagePath) => {
      if (!imagePath) return '/images/placeholder-project.png';
      
      // If it's already a full URL, return as is
      if (imagePath.startsWith('http')) {
        return imagePath;
      }
      
      // If it starts with /, it's relative to domain
      if (imagePath.startsWith('/')) {
        return `${window.location.origin}${imagePath}`;
      }
      
      // Otherwise, assume it's relative to the backend
      return `${API_URL}/${imagePath.replace(/^\//, '')}`;
    };

    // Try featured image first
    if (project.featured_image_url) {
      return constructImageUrl(project.featured_image_url);
    }
    
    // Then try gallery images
    if (project.gallery_images_urls) {
      let galleryUrls;
      
      // Parse gallery URLs if it's a string
      if (typeof project.gallery_images_urls === 'string') {
        try {
          galleryUrls = JSON.parse(project.gallery_images_urls);
        } catch (error) {
          console.error('Error parsing gallery URLs:', error);
          galleryUrls = [];
        }
      } else {
        galleryUrls = project.gallery_images_urls;
      }
      
      if (galleryUrls && galleryUrls.length > 0) {
        return constructImageUrl(galleryUrls[0]);
      }
    }
    
    // Fallback to placeholder
    return '/images/placeholder-project.png';
  };

  // Function to get category display name
  const getCategoryDisplayName = (project) => {
    return project.category_name || 'Project';
  };

  // Function to get dynamic color
  const getProjectColor = (project) => {
    if (project.card_color) {
      return project.card_color;
    }
    
    const colors = [
      '#0EA5E9', '#008080', '#231834', '#FFB006', 
      '#0489A2', '#0C090C', '#535148', '#5B7674',
      '#0D6EFD', '#EC078A', '#E8A77E', '#9374FF'
    ];
    
    const index = project.title ? project.title.length % colors.length : 0;
    return colors[index];
  };

  // Scroll navigation horizontally
  const scrollNav = (direction) => {
    if (navRef.current && allCategories.length > 5) {
      const scrollAmount = 200;
      navRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="latest-works-container">
        <h2 className="latest-works-heading">Our Latest Works</h2>
        <div className="loading-spinner">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const allCategories = [
    ...staticCategories.left,
    ...dynamicCategories // Show all dynamic categories without limit
  ];

  const shouldShowScrollButtons = allCategories.length > 5;

  return (
    <div className="latest-works-container">
      <h2 className="latest-works-heading">Our <span style={{borderBottom:"5px solid #9374FF"}}>Latest Works</span></h2>
      
      {/* Desktop Navigation with Scroll */}
      <div className="latest-works-nav-wrapper">
        {shouldShowScrollButtons && showLeftGradient && (
          <div className="nav-gradient nav-gradient-left"></div>
        )}
        
        <nav 
          ref={navRef}
          className={`latest-works-nav desktop-nav scrollable-nav ${allCategories.length > 5 ? 'more-than-five' : ''}`}
        >
          {allCategories.map(category => (
            <button 
              key={category}
              className={`nav-link ${activeFilter === category ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category)}
            >
              {category}
            </button>
          ))}
        </nav>

        {shouldShowScrollButtons && showRightGradient && (
          <div className="nav-gradient nav-gradient-right"></div>
        )}

        {/* Scroll buttons only when more than 5 categories */}
        {shouldShowScrollButtons && (
          <>
            <button 
              className="nav-scroll-button nav-scroll-left"
              onClick={() => scrollNav('left')}
              aria-label="Scroll left"
            >
              ‹
            </button>
            <button 
              className="nav-scroll-button nav-scroll-right"
              onClick={() => scrollNav('right')}
              aria-label="Scroll right"
            >
              ›
            </button>
          </>
        )}
      </div>

      {/* Mobile Dropdown Navigation */}
      <div className="mobile-nav-container" ref={dropdownRef}>
        <button className="mobile-nav-toggle" onClick={toggleDropdown}>
          <span>{activeFilter}</span>
          <svg 
            className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} 
            width="12" 
            height="8" 
            viewBox="0 0 12 8" 
            fill="none"
          >
            <path 
              d="M1 1.5L6 6.5L11 1.5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
        
        {isDropdownOpen && (
          <div className="mobile-dropdown">
            {allCategories.map(category => (
              <button
                key={category}
                className={`mobile-nav-link ${activeFilter === category ? 'active' : ''}`}
                onClick={() => handleCategorySelect(category)}
              >
                {category}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={`projects-grid ${isAnimating ? 'fade-out' : 'fade-in'}`}>
        {filteredProjects.length === 0 ? (
          <div className="no-projects-message">
            <p>No projects found in this category.</p>
          </div>
        ) : (
          filteredProjects.map((project) => (
            <div 
              key={project.id} 
              className="project-card" 
              style={{ backgroundColor: getProjectColor(project) }}
              onClick={() => handleProjectClick(project)}
            >
              <div className="project-header">
                <div className="project-title-section">
                  <span className="project-title">{project.title}</span>
                  <span className="project-category">{getCategoryDisplayName(project)}</span>
                </div>
                <span className="learn-more-text">Learn More </span>
              </div>
              <div className="project-image-placeholder">
                <img 
                  src={getProjectImage(project)} 
                  alt={project.title} 
                  className={`project-image ${isAnimating ? 'image-fade-out' : 'image-fade-in'}`}
                  onError={(e) => {
                    e.target.src = '/images/placeholder-project.png';
                  }}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom Corner Leak Particles */}
      {cornerParticles.map(particle => (
        <div
          key={particle.id}
          className={`bottom-corner-leak-particle bottom-corner-leak-particle--${particle.speed} bottom-corner-leak-particle--${particle.shape}`}
          style={{
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            background: particle.color,
            left: 0,
            top: 0,
            '--start-x': particle.startX,
            '--start-y': particle.startY,
            '--mid-x1': particle.midX1,
            '--mid-y1': particle.midY1,
            '--mid-x2': particle.midX2,
            '--mid-y2': particle.midY2,
            '--mid-x3': particle.midX3,
            '--mid-y3': particle.midY3,
            '--end-x': particle.endX,
            '--end-y': particle.endY,
            opacity: particle.opacity
          }}
        />
      ))}

      <button
        ref={buttonRef}
        className={`modern-btn ${isHovered ? 'modern-btn--hovered' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleViewAllProjects}
      >
        <span className="modern-btn__text">View All Projects ↗</span>
        
        <span className="modern-btn__border modern-btn__border--top"></span>
        <span className="modern-btn__border modern-btn__border--right"></span>
        <span className="modern-btn__border modern-btn__border--bottom"></span>
        <span className="modern-btn__border modern-btn__border--left"></span>
        
        <div className="modern-btn__particles">
          {particles.map(particle => (
            <span
              key={particle.id}
              className="modern-btn__particle"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                animationDuration: `${particle.duration}s`,
                animationDelay: `${particle.delay}s`
              }}
            />
          ))}
        </div>
        
        <span className="modern-btn__glow"></span>
      </button>
    </div>
  )
}